import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { parse } from "date-fns";
import { BASE_URL } from "../config";
import { showSuccess, showError } from "../utils/dialog";
import {
  addAppCalendarDays,
  formatAppDateTime,
  getAppDisplayTimezone,
  getAppWallDateParts,
  getAppWeekStartIso,
  instantFromAppWallParts,
  parseDbDateTime,
  toAppDateKey,
  toMysqlUtcWallString,
} from "../utils/appDateTime";
import { apiFetch } from "../utils/api";
import { getDuplicateAssignee } from "../utils/shiftAssignment";
import DateInput from "./DateInput";

const emptyCounts = () => ({
  mon: "",
  tue: "",
  wed: "",
  thu: "",
  fri: "",
  sat: "",
  sun: "",
});

const ADVANCED_DAY_ROWS = [
  { key: "mon", label: "Mon", offset: 0 },
  { key: "tue", label: "Tue", offset: 1 },
  { key: "wed", label: "Wed", offset: 2 },
  { key: "thu", label: "Thu", offset: 3 },
  { key: "fri", label: "Fri", offset: 4 },
  { key: "sat", label: "Sat", offset: 5 },
  { key: "sun", label: "Sun", offset: 6 },
];

const REQUIRED_DAY_MSG = "At least one shift must exist on this day";

/** Grid key (mon–sun) for the shift start in app display timezone. */
function getShiftSourceDayKey(baseStart) {
  if (!baseStart || isNaN(baseStart.getTime())) return null;
  const key = toAppDateKey(baseStart);
  if (!key) return null;
  const anchor = parseDbDateTime(`${key}T12:00:00Z`);
  if (!anchor) return null;
  const weekday = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    timeZone: getAppDisplayTimezone(),
  }).format(anchor);
  const dayKeyMap = {
    Sun: "sun",
    Mon: "mon",
    Tue: "tue",
    Wed: "wed",
    Thu: "thu",
    Fri: "fri",
    Sat: "sat",
  };
  return dayKeyMap[weekday] ?? null;
}

function sumAdvancedDayCounts(counts) {
  return ADVANCED_DAY_ROWS.reduce((acc, { key }) => {
    const n = Number(counts[key]);
    return acc + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);
}

function getMondayStartOfWeekContaining(date) {
  const weekStartKey = getAppWeekStartIso(toAppDateKey(date));
  return parseDbDateTime(`${weekStartKey}T12:00:00Z`);
}

function shiftInstantToMysql(d) {
  if (!d || isNaN(d.getTime())) return "";
  return toMysqlUtcWallString(d);
}

/** Parse shift datetime from API (UTC wall naive or ISO). */
function parseShiftDateForDuplicate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  const fromDb = parseDbDateTime(raw);
  if (fromDb) return fromDb;
  const uiParsed = parse(raw, "MMM d, yyyy h:mm a", new Date());
  if (!isNaN(uiParsed.getTime())) return uiParsed;
  return null;
}

function duplicateInstantOnCalendarDate(baseInstant, isoDate) {
  const wall = getAppWallDateParts(baseInstant);
  if (!wall) return null;
  const parts = String(isoDate || "")
    .trim()
    .split("-")
    .map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  const [year, month, day] = parts;
  return instantFromAppWallParts({
    year,
    month,
    day,
    hour: wall.hour,
    minute: wall.minute,
    second: wall.second ?? 0,
  });
}

function primeDuplicateStateFromShift(currentShift, setters) {
  const {
    setDuplicateDate,
    setDuplicateCounts,
    setDuplicateMethod,
    setAssignStaff,
  } = setters;
  const baseStart = parseShiftDateForDuplicate(currentShift?.start_date);
  if (baseStart && !isNaN(baseStart.getTime())) {
    setDuplicateDate(addAppCalendarDays(toAppDateKey(baseStart), 1));
    // Advanced grid: default "1" on the shift's start weekday (legacy required day).
    const dayKey = getShiftSourceDayKey(baseStart);
    setDuplicateCounts(
      dayKey ? { ...emptyCounts(), [dayKey]: "1" } : emptyCounts(),
    );
  } else {
    setDuplicateDate("");
    setDuplicateCounts(emptyCounts());
  }
  setDuplicateMethod("simple");
  if (typeof setAssignStaff === "function") {
    setAssignStaff(Boolean(getDuplicateAssignee(currentShift)));
  }
}

function normalizeSkillIds(shift) {
  const raw = shift?.required_skills;
  if (Array.isArray(raw)) {
    return raw
      .map((s) => (typeof s === "object" ? s.id || s.skill_id : Number(s)))
      .filter((id) => Number.isFinite(Number(id)) && Number(id) > 0)
      .map(Number);
  }
  if (typeof raw === "string" && raw.trim()) {
    return raw
      .split(",")
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0);
  }
  return [];
}

function normalizeCertIds(shift) {
  const raw = shift?.required_certifications;
  if (Array.isArray(raw)) {
    return raw
      .map((c) =>
        typeof c === "object" ? c.id || c.certification_id : Number(c),
      )
      .filter((id) => Number.isFinite(Number(id)) && Number(id) > 0)
      .map(Number);
  }
  if (typeof raw === "string" && raw.trim()) {
    return raw
      .split(",")
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0);
  }
  return [];
}

/**
 * Standalone or embedded "Duplicate Shift" dialog (Simple / Advanced).
 * - Standalone: pass open + shiftId only; shift is fetched when opened.
 * - Embedded: pass shift + shiftId from ShiftDetailModal (skip fetch).
 */
const DuplicateShiftModal = ({
  open,
  onClose,
  shiftId,
  /** When set, skip network load (parent already has full shift) */
  shift: shiftProp,
  /** Merged over shift for unsaved form fields (Shift detail embedded mode) */
  shiftPatch,
  /** If provided, called instead of default PUT (e.g. parent handleSave) */
  onSaveBeforeDuplicate,
  requestDefaults,
  /** After successful duplicate (refresh lists, etc.) */
  onDuplicateSuccess,
  zIndexClass = "z-[10001]",
}) => {
  const [fetchedShift, setFetchedShift] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loadingShift, setLoadingShift] = useState(false);
  const [duplicateMethod, setDuplicateMethod] = useState("simple");
  const [duplicateDate, setDuplicateDate] = useState("");
  const [duplicateSubmitting, setDuplicateSubmitting] = useState(false);
  const [duplicateCounts, setDuplicateCounts] = useState(emptyCounts);
  const [assignStaff, setAssignStaff] = useState(true);
  const primedForOpenRef = useRef(false);

  const rawShift = shiftProp ?? fetchedShift;
  const shift = useMemo(() => {
    if (!rawShift) return null;
    if (!shiftPatch || typeof shiftPatch !== "object") return rawShift;
    const { start_date: _patchStart, end_date: _patchEnd, ...restPatch } =
      shiftPatch;
    return { ...rawShift, ...restPatch };
  }, [rawShift, shiftPatch]);
  const effectiveShiftId = shiftId ?? shift?.shift_id ?? shift?.id;
  const duplicateAssignee = useMemo(
    () => (shift ? getDuplicateAssignee(shift) : null),
    [shift],
  );

  const loadShift = useCallback(async () => {
    if (!shiftId) return;
    setLoadingShift(true);
    setLoadError("");
    try {
      const response = await fetch(`${BASE_URL}/api/jobs/shifts/${shiftId}`, {
        credentials: "include",
      });
      const data = await response.json();
      const resolved = data.data || data || {};
      const nextShift = { ...resolved };
      const fallbackContactId =
        requestDefaults?.contactAccountId ??
        requestDefaults?.contact_account_id ??
        null;
      const fallbackContactName =
        requestDefaults?.contactName ?? requestDefaults?.contact ?? "";
      const fallbackSupervisorId =
        requestDefaults?.supervisorContactAccountId ??
        requestDefaults?.supervisor_account_id ??
        requestDefaults?.supervisor_contact_account_id ??
        null;
      const fallbackSupervisorName =
        requestDefaults?.supervisorName ??
        requestDefaults?.supervisor ??
        requestDefaults?.supervisorLabel ??
        "";
      if (!nextShift.contact_account_id && fallbackContactId) {
        nextShift.contact_account_id = fallbackContactId;
      }
      if (!nextShift.contact_name && fallbackContactName) {
        nextShift.contact_name = fallbackContactName;
      }
      if (!nextShift.supervisor_contact_account_id && fallbackSupervisorId) {
        nextShift.supervisor_contact_account_id = fallbackSupervisorId;
      }
      if (!nextShift.supervisor_name && fallbackSupervisorName) {
        nextShift.supervisor_name = fallbackSupervisorName;
      }
      setFetchedShift(nextShift);
    } catch (e) {
      console.error("DuplicateShiftModal load:", e);
      setLoadError("Failed to load shift");
      setFetchedShift(null);
    } finally {
      setLoadingShift(false);
    }
  }, [shiftId, requestDefaults]);

  useEffect(() => {
    if (!open) {
      primedForOpenRef.current = false;
      setFetchedShift(null);
      setLoadError("");
      setLoadingShift(false);
      return;
    }
    if (shiftProp) return;
    if (shiftId) loadShift();
  }, [open, shiftId, shiftProp, loadShift]);

  useEffect(() => {
    if (!open || !rawShift || primedForOpenRef.current) return;
    primedForOpenRef.current = true;
    primeDuplicateStateFromShift(rawShift, {
      setDuplicateDate,
      setDuplicateCounts,
      setDuplicateMethod,
      setAssignStaff,
    });
  }, [open, rawShift]);

  const weekState = String(shift?.week_state || "").toLowerCase();
  const closedWeekFlag =
    shift?.isClosedWeek === true ||
    shift?.isClosedWeek === 1 ||
    shift?.isClosedWeek === "1";
  const isClosedWeek = closedWeekFlag || weekState === "closed";

  const buildShiftPayload = useCallback(
    (startDate, endDate) => {
      if (!shift) return null;
      const requiredSkills = normalizeSkillIds(shift);
      const requiredCerts = normalizeCertIds(shift);
      const unpaid =
        Number(shift?.unpaid_break || 0) > 0 ? Number(shift.unpaid_break) : 0;
      return {
        staff_request_id: shift.staff_request_id,
        division_id: shift.division_id,
        position_id: shift.position_id,
        start_date: shiftInstantToMysql(startDate),
        end_date: shiftInstantToMysql(endDate),
        pay_rate: parseFloat(shift?.pay_rate || 0),
        bill_rate: parseFloat(shift?.bill_rate || 0),
        unpaid_break: unpaid,
        uniform: shift?.uniform || "",
        required_skills: requiredSkills,
        required_certifications: requiredCerts,
      };
    },
    [shift],
  );

  const saveShiftSilently = useCallback(async () => {
    if (!shift || !effectiveShiftId) return false;
    if (isClosedWeek) {
      showError(
        "This shift belongs to a closed accounting week and cannot be edited.",
      );
      return false;
    }
    try {
      const updateData = {
        state: shift.state || "unassigned",
        start_date: shift.start_date,
        end_date: shift.end_date,
        pay_rate: shift.pay_rate ?? "",
        bill_rate: shift.bill_rate ?? "",
        unpaid_break: Number(shift.unpaid_break || 0) > 0 ? 1 : 0,
        gender: shift.gender || "any",
        admin_note: shift.admin_note || "",
        note_to_staff: shift.note || "",
        division_id: shift.division_id,
        position_id: shift.position_id,
        travel_time_amount: shift.travel_time_amount || "",
        travel_time_type: shift.travel_time_type || "",
        notify_staff_change: shift.notify_staff_change === 1,
        uniform: shift.uniform || "",
        required_skills: normalizeSkillIds(shift),
        required_certifications: normalizeCertIds(shift),
      };
      if (shift.supervisor_contact_account_id) {
        updateData.supervisor_account_id = shift.supervisor_contact_account_id;
      }
      const response = await apiFetch(
        `/api/jobs/shifts/${effectiveShiftId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updateData),
        },
      );
      if (response.ok) return true;
      const errorData = await response.text();
      showError("Failed to save shift: " + errorData);
      return false;
    } catch (error) {
      showError("Error saving shift: " + error.message);
      return false;
    }
  }, [shift, effectiveShiftId, isClosedWeek]);

  const handleDuplicateSubmit = async () => {
    if (!shift || duplicateSubmitting) return;
    if (
      !shift?.staff_request_id ||
      !shift?.position_id ||
      !shift?.division_id
    ) {
      showError("Shift is missing required fields for duplication.");
      return;
    }

    let saved = false;
    let savedSchedule = null;
    if (typeof onSaveBeforeDuplicate === "function") {
      const saveResult = await onSaveBeforeDuplicate();
      if (!saveResult) return;
      saved = true;
      if (saveResult && typeof saveResult === "object" && saveResult.start_date) {
        savedSchedule = saveResult;
      }
    } else {
      saved = await saveShiftSilently();
    }
    if (!saved) return;

    const scheduleStartRaw =
      savedSchedule?.start_date ?? rawShift?.start_date ?? shift?.start_date;
    const scheduleEndRaw =
      savedSchedule?.end_date ?? rawShift?.end_date ?? shift?.end_date;

    const baseStart = scheduleStartRaw
      ? parseShiftDateForDuplicate(scheduleStartRaw)
      : null;
    const baseEnd = scheduleEndRaw
      ? parseShiftDateForDuplicate(scheduleEndRaw)
      : null;
    if (!baseStart || !baseEnd || isNaN(baseStart) || isNaN(baseEnd)) {
      showError("Shift dates are invalid for duplication.");
      return;
    }

    let durationMs = baseEnd.getTime() - baseStart.getTime();
    if ((!durationMs || durationMs <= 0) && shift?.duration_minutes != null) {
      const minutes = Number(shift.duration_minutes);
      if (Number.isFinite(minutes) && minutes > 0) {
        durationMs = minutes * 60 * 1000;
      }
    }
    if ((!durationMs || durationMs <= 0) && shift?.durationMinutes != null) {
      const minutes = Number(shift.durationMinutes);
      if (Number.isFinite(minutes) && minutes > 0) {
        durationMs = minutes * 60 * 1000;
      }
    }
    if (!durationMs || durationMs <= 0) {
      durationMs = 60 * 60 * 1000;
    }

    setDuplicateSubmitting(true);
    try {
      if (duplicateMethod === "simple") {
        if (!duplicateDate) {
          showError("Please choose a date to duplicate.");
          return;
        }
        const newStart = duplicateInstantOnCalendarDate(baseStart, duplicateDate);
        if (!newStart || isNaN(newStart.getTime())) {
          showError("Invalid duplicate date.");
          return;
        }
        const newEnd = new Date(newStart.getTime() + durationMs);
        const startMysql = shiftInstantToMysql(newStart);
        const endMysql = shiftInstantToMysql(newEnd);

        const shouldAssignStaff =
          assignStaff && Boolean(duplicateAssignee?.accountId);

        if (shouldAssignStaff) {
          // Do not exclude the source shift: duplicating into the same timeslot must
          // conflict with the existing assignment on that shift.
          const availRes = await apiFetch(
            `/api/schedules/check-staff-availability`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                accountIds: [duplicateAssignee.accountId],
                startDatetime: startMysql,
                endDatetime: endMysql,
              }),
            },
          );
          const availData = await availRes.json().catch(() => ({}));
          const conflicts = Array.isArray(availData?.conflicts)
            ? availData.conflicts
            : [];
          if (!availRes.ok) {
            throw new Error(
              availData?.message || "Failed to verify staff availability.",
            );
          }
          if (conflicts.length > 0) {
            showError(
              `Note: Cannot Assign ${duplicateAssignee.displayName} to Duplicated Shift`,
              "Attention",
            );
            return;
          }
        }

        const payload = buildShiftPayload(newStart, newEnd);
        const res = await apiFetch(`/api/jobs/shifts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const err = await res.text();
          throw new Error(err || "Failed to duplicate shift.");
        }
        const createData = await res.json().catch(() => ({}));
        const newShiftId = Number(createData?.id ?? createData?.shift_id);
        if (shouldAssignStaff && Number.isFinite(newShiftId) && newShiftId > 0) {
          const assignRes = await apiFetch(`/api/shifts/${newShiftId}/assign`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ accountId: duplicateAssignee.accountId }),
          });
          if (!assignRes.ok) {
            const assignErr = await assignRes.json().catch(() => ({}));
            throw new Error(
              assignErr?.message ||
                "Shift was created but staff could not be assigned.",
            );
          }
        }
      } else if (duplicateMethod === "advanced") {
        const sourceDayKey = getShiftSourceDayKey(baseStart);
        const totalShifts = sumAdvancedDayCounts(duplicateCounts);
        if (!totalShifts) {
          showError("Please set at least one day count.");
          return;
        }
        if (sourceDayKey) {
          const requiredCount = Number(duplicateCounts[sourceDayKey] || 0);
          if (!Number.isFinite(requiredCount) || requiredCount < 1) {
            showError(REQUIRED_DAY_MSG);
            return;
          }
        }
        const startOfWeek = getMondayStartOfWeekContaining(baseStart);
        const weekStartKey = startOfWeek ? toAppDateKey(startOfWeek) : null;
        const requests = [];
        ADVANCED_DAY_ROWS.forEach(({ key, offset }) => {
          const count = Number(duplicateCounts[key] || 0);
          if (!count || count <= 0) return;
          // Legacy: the shift's own day already counts as 1 existing — POST count - 1.
          const copiesToCreate =
            key === sourceDayKey ? Math.max(0, count - 1) : count;
          if (copiesToCreate <= 0) return;
          const targetDateKey = weekStartKey
            ? addAppCalendarDays(weekStartKey, offset)
            : null;
          const dayAnchor = targetDateKey
            ? duplicateInstantOnCalendarDate(baseStart, targetDateKey)
            : null;
          if (!dayAnchor || isNaN(dayAnchor.getTime())) return;
          const dayEnd = new Date(dayAnchor.getTime() + durationMs);
          for (let i = 0; i < copiesToCreate; i++) {
            const p = buildShiftPayload(dayAnchor, dayEnd);
            if (p) requests.push(p);
          }
        });
        // 1 existing + 0 new (only required day = 1): no POSTs; still success (legacy).
        for (const payload of requests) {
          const res = await apiFetch(`/api/jobs/shifts`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          if (!res.ok) {
            const err = await res.text();
            throw new Error(err || "Failed to duplicate shift.");
          }
        }
      } else {
        showError("Select a duplication method.");
        return;
      }

      showSuccess("Shift duplicated successfully!");
      onClose();
      if (typeof onDuplicateSuccess === "function") onDuplicateSuccess();
    } catch (error) {
      console.error("Duplicate shift failed:", error);
      showError(error?.message || "Failed to duplicate shift.");
    } finally {
      setDuplicateSubmitting(false);
    }
  };

  const duplicateShiftTitle = shift
    ? [shift.client_name, shift.position_name || shift.position]
        .filter(Boolean)
        .join(" - ") || `Shift #${shift.shift_id || effectiveShiftId || ""}`
    : "";
  let duplicateScheduleText = "—";
  if (shift?.start_date && shift?.end_date) {
    const s = formatAppDateTime(shift.start_date, { emptyValue: "" });
    const e = formatAppDateTime(shift.end_date, { emptyValue: "" });
    if (s && e) {
      duplicateScheduleText = `${s} - ${e}`;
    }
  }
  const duplicateSupervisorLabel =
    (shift?.supervisor_name || shift?.supervisor || "").trim() || "—";
  const duplicatePayDisplay =
    shift?.pay_rate != null ? String(shift.pay_rate) : "—";
  const duplicateBillDisplay =
    shift?.bill_rate != null ? String(shift.bill_rate) : "—";

  const shiftSourceDayKey = useMemo(() => {
    if (!shift?.start_date) return null;
    const baseStart = parseShiftDateForDuplicate(shift.start_date);
    return getShiftSourceDayKey(baseStart);
  }, [shift?.start_date]);

  const advancedDuplicatePreview = useMemo(() => {
    if (duplicateMethod !== "advanced" || !shift?.start_date) return null;
    const totalShifts = sumAdvancedDayCounts(duplicateCounts);
    if (totalShifts === 0) return { existingCount: 0, newCount: 0 };
    return { existingCount: 1, newCount: totalShifts - 1 };
  }, [duplicateMethod, shift?.start_date, duplicateCounts]);

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 ${zIndexClass} flex items-center justify-center p-4`}
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start gap-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Duplicate Shift
            </h3>
            <button
              type="button"
              onClick={() => !duplicateSubmitting && onClose()}
              className="text-gray-500 hover:text-gray-700 text-xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          {(loadingShift || loadError) && !shiftProp && (
            <div className="mt-4 text-sm text-gray-600">
              {loadingShift ? "Loading shift…" : loadError}
            </div>
          )}
          {shift && (
            <div className="mt-4 space-y-2 text-sm text-gray-800">
              <div className="font-medium text-base text-gray-900">
                {duplicateShiftTitle || "Shift"}
              </div>
              <div className="text-gray-700">{duplicateScheduleText}</div>
              <div>
                <span className="text-gray-500">Supervisor: </span>
                {duplicateSupervisorLabel}
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                <span>
                  <span className="text-gray-500">Pay Rate: </span>
                  {duplicatePayDisplay}
                </span>
                <span>
                  <span className="text-gray-500">Bill Rate: </span>
                  {duplicateBillDisplay}
                </span>
              </div>
            </div>
          )}
        </div>

        {shift && (
          <>
            <div className="p-6 space-y-5">
              <div>
                <div className="text-sm font-medium text-gray-800 mb-2">
                  Duplication Method
                </div>
                <div className="flex flex-wrap gap-6">
                  <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name="dupMethodStandalone"
                      checked={duplicateMethod === "simple"}
                      onChange={() => {
                        setDuplicateMethod("simple");
                      }}
                      className="text-red-600 focus:ring-red-500"
                    />
                    <span>Simple</span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name="dupMethodStandalone"
                      checked={duplicateMethod === "advanced"}
                      onChange={() => {
                        setDuplicateMethod("advanced");
                        setAssignStaff(false);
                      }}
                      className="text-red-600 focus:ring-red-500"
                    />
                    <span>Advanced</span>
                  </label>
                </div>
              </div>

              {duplicateMethod === "simple" ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Create a duplicate on
                      <span className="text-red-600">*</span>
                    </label>
                    <DateInput
                      value={duplicateDate || ""}
                      onChange={setDuplicateDate}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-900"
                    />
                  </div>
                  {duplicateAssignee ? (
                    <label className="inline-flex items-start gap-2 cursor-pointer text-sm text-gray-800">
                      <input
                        type="checkbox"
                        checked={assignStaff}
                        onChange={(e) => setAssignStaff(e.target.checked)}
                        disabled={duplicateSubmitting}
                        className="mt-0.5 text-red-600 focus:ring-red-500 rounded"
                      />
                      <span>
                        Assign {duplicateAssignee.displayName} to Duplicated
                        Shift
                      </span>
                    </label>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Does not include staff assignment.
                  </p>
                  <p className="text-sm text-gray-600">
                    Create one or many copies on multiple days in the{" "}
                    <span className="font-medium">week that contains this shift</span>
                    . For each day, the number is how many shifts use the same start
                    and end time as the original (not back-to-back blocks).
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {ADVANCED_DAY_ROWS.map(({ key, label }) => {
                      const isRequiredDay = shiftSourceDayKey === key;
                      const countNum = Number(duplicateCounts[key]);
                      const showDayError =
                        isRequiredDay &&
                        (!Number.isFinite(countNum) || countNum < 1);
                      return (
                        <div key={key} className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <label className="w-10 text-sm text-gray-700 shrink-0">
                              {label}
                            </label>
                            <input
                              type="number"
                              min={isRequiredDay ? 1 : 0}
                              step={1}
                              placeholder="0"
                              value={duplicateCounts[key]}
                              onChange={(e) =>
                                setDuplicateCounts((prev) => ({
                                  ...prev,
                                  [key]: e.target.value,
                                }))
                              }
                              className={`flex-1 border rounded-md px-3 py-2 text-sm ${
                                showDayError
                                  ? "border-red-500 focus:ring-red-500"
                                  : "border-gray-300"
                              }`}
                              aria-invalid={showDayError || undefined}
                            />
                          </div>
                          {showDayError ? (
                            <p className="text-xs text-red-600 pl-12">
                              {REQUIRED_DAY_MSG}
                            </p>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                  {advancedDuplicatePreview != null && (
                    <div className="rounded-md bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-800">
                      <div className="font-medium">
                        {advancedDuplicatePreview.existingCount} existing +{" "}
                        {advancedDuplicatePreview.newCount} new
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 pt-0 flex justify-end gap-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => !duplicateSubmitting && onClose()}
                disabled={duplicateSubmitting}
                className="px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDuplicateSubmit}
                disabled={duplicateSubmitting}
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {duplicateSubmitting ? "Working…" : "Duplicate"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DuplicateShiftModal;
